import { createClient } from '@supabase/supabase-js';
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '../config.ts';

// Un singur client, cu sesiunea utilizatorului. Schemele: `app` (firma, echipa, alerte, RPC-urile
// de cautare), `seap` (nomenclatoarele, achizitiile — RLS pe istoricul planului).
export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

export const app = () => supabase.schema('app');
export const seap = () => supabase.schema('seap');

/** Rezultatul unei interogari Supabase, sau eroarea ei aruncata (pentru TanStack Query). */
export async function must<T>(
  q: PromiseLike<{ data: T | null; error: { message: string } | null }>,
): Promise<T> {
  const { data, error } = await q;
  if (error) throw new Error(friendly(error.message));
  return data as T;
}

/** Un apel RPC din schema app. */
export function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  return must(app().rpc(fn, args)) as Promise<T>;
}

/** Edge Function (plata, invitatii, ANAF): mesajul de eroare din corpul raspunsului. */
export async function callFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let message = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const j = (await ctx.json()) as { error?: string };
        if (j.error) message = j.error;
      } catch {
        /* corp fara JSON */
      }
    }
    throw new Error(friendly(message));
  }
  return data as T;
}

// Mesajele SQL sunt fara diacritice (comentariile din cod); cele mai dese, traduse frumos.
const MESSAGES: [RegExp, string][] = [
  [/Invalid login credentials/i, 'Email sau parolă greșită.'],
  [
    /User is banned|user_banned/i,
    'Contul e blocat. Pentru detalii, scrie-ne pe emailul din pagina de confidențialitate.',
  ],
  [/Email not confirmed/i, 'Confirmă întâi adresa de email (linkul primit pe email).'],
  [
    /User already registered/i,
    'Există deja un cont cu acest email. Intră în cont sau resetează parola.',
  ],
  [/Password should be at least/i, 'Parola trebuie să aibă cel puțin 8 caractere.'],
  [/captcha/i, 'Verificarea anti-robot a eșuat. Reîncearcă.'],
  [/Error sending .*email/i, 'Emailul nu a putut fi trimis acum. Reîncearcă în câteva minute.'],
  [
    /rate limit|too many requests/i,
    'Prea multe încercări într-un timp scurt. Reîncearcă peste câteva minute.',
  ],
  [
    /Firma cu CUI (\d+) are deja cont/,
    'Firma cu CUI $1 are deja cont în Elsa. Cere unui coleg o invitație.',
  ],
  [/Nu ai dreptul ([\w.]+)/, 'Rolul tău din firmă nu permite această acțiune.'],
  [
    /Firma a folosit toate locurile/,
    'Firma a folosit toate locurile din plan. Mărește planul sau scoate un membru.',
  ],
  [/Firma a folosit toate alertele/, 'Ai atins numărul maxim de alerte din plan.'],
  [/Plafonul de export/, 'Ai atins plafonul de export pe luna aceasta.'],
  [/Firma nu are un abonament activ/, 'Firma nu are un abonament activ.'],
  [/Invitatia nu exista sau a expirat/, 'Invitația nu există sau a expirat. Cere una nouă.'],
  [/Invitatia e pentru alta adresa/, 'Invitația e pentru altă adresă de email.'],
  [/transfera intai proprietatea/, 'Firma are alți membri: transferă întâi proprietatea (Echipă).'],
  [/anuleaza-l intai/, 'Firma are un abonament activ: anulează-l întâi din portalul de plată.'],
  [
    /Failed to fetch|NetworkError/i,
    'Nu există conexiune cu serverul. Verifică internetul și reîncearcă.',
  ],
];

export function friendly(message: string): string {
  for (const [re, text] of MESSAGES) {
    const m = message.match(re);
    if (m) return text.replace(/\$(\d)/g, (_, i: string) => m[Number(i)] ?? '');
  }
  return message;
}
